import hasOwnKey from '@/utils/hasOwnKey';

import { usableAuditFilter } from './filterRules';
import { isRecord } from './mappers';
import { changePairs } from './summary';
import type { AuditText } from './text';
import type { AuditLogFilters } from './types';
import { type AuditValue, auditValueFilterText, auditValueText, toAuditValue } from './values';

// Enough for a list of ids or a small placement; the raw JSON below the table has the rest.
const CONTAINER_TEXT_LENGTH = 200;

/** A value from `changes` as the dialog shows it in full. */
export interface AuditDetailValue {
    text: AuditText;
    /** The stored form when the text differs: a date's ISO text, or JSON cut short. */
    title: string | null;
}

export interface AuditChangeCell extends AuditDetailValue {
    /** Null for values no equality filter matches: empty, hidden, lists and objects. */
    filter: AuditLogFilters | null;
}

export interface AuditChangeRow {
    field: string;
    /** Sets `changedField`; null for a key the backend cannot match, such as a cut marker. */
    filter: AuditLogFilters | null;
    /** Null when the entry recorded nothing on this side, as a delete keeps no after state. */
    before: AuditChangeCell | null;
    after: AuditChangeCell | null;
    /** False for a field written with its old value, such as a placement's contract. */
    changed: boolean;
}

export type AuditChangesTable =
    | { kind: 'none' }
    /** A shape the Field / Before / After table cannot show; only the raw JSON can. */
    | { kind: 'raw' }
    | { kind: 'table'; rows: AuditChangeRow[] };

type Side = 'before' | 'after';

/** A value in full, with a date's stored ISO text in the title. */
export function auditValueDetail(value: AuditValue): AuditDetailValue {
    return { text: auditValueText(value), title: value.kind === 'date' ? value.text : null };
}

/** Lists and objects show as compact JSON rather than "2 items", so ids stay readable. */
function detailValue(raw: unknown): AuditDetailValue {
    if (Array.isArray(raw) || isRecord(raw)) {
        const json = JSON.stringify(raw);
        return json.length > CONTAINER_TEXT_LENGTH
            ? { text: `${json.slice(0, CONTAINER_TEXT_LENGTH)}…`, title: json }
            : { text: json, title: null };
    }
    return auditValueDetail(toAuditValue(raw));
}

function cell(field: string, raw: unknown, side: Side): AuditChangeCell {
    const text = auditValueFilterText(toAuditValue(raw));
    let filter: AuditLogFilters | null = null;
    if (text !== null) {
        filter = usableAuditFilter(
            side === 'before'
                ? { changedField: field, changedFrom: text }
                : { changedField: field, changedTo: text }
        );
    }
    return { ...detailValue(raw), filter };
}

function sideCell(
    record: Record<string, unknown>,
    field: string,
    side: Side
): AuditChangeCell | null {
    return hasOwnKey(record, field) ? cell(field, record[field], side) : null;
}

function diffRows(
    before: Record<string, unknown>,
    after: Record<string, unknown>
): AuditChangeRow[] {
    return changePairs(before, after).map(({ field, changed }) => ({
        field,
        filter: usableAuditFilter({ changedField: field }),
        before: sideCell(before, field, 'before'),
        after: sideCell(after, field, 'after'),
        changed,
    }));
}

/** A delete snapshot fills Before only; a value written without a before state, After only. */
function oneSideRows(record: Record<string, unknown>, side: Side): AuditChangeRow[] {
    return Object.keys(record).map((field) => {
        const recorded = cell(field, record[field], side);
        return {
            field,
            filter: usableAuditFilter({ changedField: field }),
            before: side === 'before' ? recorded : null,
            after: side === 'after' ? recorded : null,
            changed: true,
        };
    });
}

function isEmptyRecord(value: unknown): boolean {
    return isRecord(value) && Object.keys(value).length === 0;
}

function tableRows(before: unknown, after: unknown): AuditChangeRow[] {
    const noBefore = before === null || before === undefined || isEmptyRecord(before);
    if (isRecord(before) && !noBefore) {
        if (isRecord(after)) return diffRows(before, after);
        return after === null || after === undefined ? oneSideRows(before, 'before') : [];
    }
    return noBefore && isRecord(after) ? oneSideRows(after, 'after') : [];
}

/**
 * Every field of an entry's `changes`, unchanged ones included, for the dialog's table.
 * A diff fills both sides, a delete snapshot (and `bioscope.move.undo`) only Before, and
 * a first write only After.
 */
export function describeAuditChanges(changes: Record<string, unknown> | null): AuditChangesTable {
    if (changes === null) return { kind: 'none' };
    const rows = tableRows(changes.before, changes.after);
    return rows.length > 0 ? { kind: 'table', rows } : { kind: 'raw' };
}
